import React, { useState } from "react";
import { Lead, Section, P, Callout, CTA } from "../../../../blog/components";

export const title = "The benchmarks are fake";
export const excerpt =
  "A Berkeley team scored ~100% on six of eight top agent benchmarks without solving a single task. On one, the string {} passed all 890. The number was never measuring the agent.";
export const heroAlt = "Moebius-style landscape: a red sun rising behind low clouds over a vast pale plain, seen from a rocky cliff edge with two small figures on the rim";
export const readingTime = "5 min read";

const BLUE = "#5ba8f5";
const FAIL = "#e0795f";
/** Handwriting face (loaded in index.html) with safe fallbacks, for the sketched figures. */
const HAND = '"Architects Daughter", "Comic Sans MS", "Segoe Print", cursive';
/** Asymmetric border-radii that read as a hand-drawn rectangle; rotate through them for variety. */
const WONKY = [
  "255px 15px 225px 15px / 15px 225px 15px 255px",
  "15px 225px 18px 255px / 255px 18px 225px 15px",
  "225px 15px 255px 18px / 18px 255px 15px 225px",
  "15px 255px 15px 225px / 225px 15px 255px 18px",
];
const wonk = (i: number) => WONKY[((i % WONKY.length) + WONKY.length) % WONKY.length];
const tilt = (i: number) => [-0.6, 0.5, -0.4, 0.7, -0.3][i % 5];

/** In-text footnote marker: a real link to the cited source (opens in a new tab). */
function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BLUE, textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

/* ── Sketched visuals ─────────────────────────────────────────────────────────
   Same hand-drawn (Excalidraw-ish) look as the rest of the blog: a handwriting
   face, wobbly borders via asymmetric border-radii. Labels stay un-roughened so
   they read clearly, and are never set in all-caps.
   ───────────────────────────────────────────────────────────────────────────── */

/** Wraps a graphic in a hand-drawn card with an optional handwritten caption. */
function Figure({ children, caption, i = 0 }: { children: React.ReactNode; caption?: string; i?: number }) {
  return (
    <figure style={{ margin: "34px 0" }}>
      <div
        style={{
          padding: "22px 18px",
          borderRadius: wonk(i),
          border: "2px solid var(--text-muted)",
          background: "var(--surface-4)",
        }}
      >
        {children}
      </div>
      {caption && (
        <figcaption
          style={{ marginTop: 12, fontFamily: HAND, fontSize: 16, color: "var(--text-muted)", textAlign: "center", lineHeight: 1.5 }}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/**
 * Interactive mock eval harness. Pick a lazy answer, watch the score sit at
 * 100%, then flip "actually read the answer" on and watch it collapse to zero.
 * This is the FieldWorkArena bug in miniature: the validator only checked that
 * the last message came from the assistant, never what the message said.
 */
function EvalHarness() {
  const ANSWERS = [
    { id: "obj", label: "{}", note: "an empty object" },
    { id: "done", label: '"I’m done"', note: "no work attached" },
    { id: "blank", label: "(empty message)", note: "nothing at all" },
  ];
  const [picked, setPicked] = useState("obj");
  const [reads, setReads] = useState(false);
  const score = reads ? 0 : 100;
  const colour = reads ? FAIL : BLUE;
  const current = ANSWERS.find((a) => a.id === picked) ?? ANSWERS[0]!;

  return (
    <div style={{ fontFamily: HAND }}>
      <div style={{ fontSize: 16, color: "var(--text-secondary)", marginBottom: 8 }}>
        Submit an answer to all 890 tasks
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
        {ANSWERS.map((a, i) => {
          const on = a.id === picked;
          return (
            <button
              key={a.id}
              onClick={() => setPicked(a.id)}
              style={{
                cursor: "pointer",
                padding: "8px 14px",
                fontFamily: HAND,
                fontSize: 16,
                borderRadius: wonk(i),
                color: on ? "#fff" : "var(--text-primary)",
                background: on ? BLUE : "var(--surface-5)",
                border: `1.8px solid ${on ? BLUE : "var(--border-strong)"}`,
                transform: `rotate(${tilt(i) * 0.6}deg)`,
              }}
            >
              {a.label}
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "center",
          gap: 16,
          padding: "16px 18px",
          borderRadius: wonk(2),
          border: "2px solid var(--border-strong)",
          background: "var(--surface-5)",
        }}
      >
        <div>
          <div style={{ fontSize: 15, color: "var(--text-muted)", marginBottom: 6 }}>The validator runs:</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
            pass = last_message.role == "assistant"
            {reads && (
              <>
                <br />
                <span style={{ color: FAIL }}>&amp;&amp; answer_is_correct(last_message)</span>
              </>
            )}
          </div>
          <div style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 8 }}>
            you sent {current.label}, {current.note}
          </div>
        </div>
        <div style={{ textAlign: "center", minWidth: 96 }}>
          <div style={{ fontSize: 52, lineHeight: 1, color: colour, fontWeight: 700 }}>{score}%</div>
          <div style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 2 }}>
            {reads ? "tasks solved: 0" : "resolve rate"}
          </div>
        </div>
      </div>

      <button
        onClick={() => setReads((r) => !r)}
        style={{
          cursor: "pointer",
          marginTop: 14,
          display: "flex",
          alignItems: "center",
          gap: 12,
          width: "100%",
          padding: "11px 16px",
          fontFamily: HAND,
          fontSize: 16,
          textAlign: "left",
          color: "var(--text-primary)",
          borderRadius: wonk(3),
          background: "var(--surface-4)",
          border: `2px solid ${reads ? FAIL : "var(--border-strong)"}`,
        }}
      >
        <span
          aria-hidden
          style={{
            position: "relative",
            width: 46,
            height: 26,
            borderRadius: 999,
            background: reads ? FAIL : "var(--border-strong)",
            transition: "background 120ms",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 3,
              left: reads ? 23 : 3,
              width: 20,
              height: 20,
              borderRadius: 999,
              background: "#fff",
              transition: "left 120ms",
            }}
          />
        </span>
        actually read the answer: {reads ? "on" : "off"}
      </button>
    </div>
  );
}

/** Sketched bar chart: the eight benchmarks an exploit agent gamed, and the score it got on each. */
function ScoreBars() {
  const rows = [
    { name: "Terminal-Bench", tasks: "89 tasks", score: 100 },
    { name: "SWE-bench Verified", tasks: "500 tasks", score: 100 },
    { name: "SWE-bench Pro", tasks: "731 tasks", score: 100 },
    { name: "FieldWorkArena", tasks: "890 tasks", score: 100 },
    { name: "CAR-bench", tasks: "all tasks", score: 100 },
    { name: "WebArena", tasks: "812 tasks", score: 100 },
    { name: "GAIA", tasks: "165 tasks", score: 98 },
    { name: "OSWorld", tasks: "369 tasks", score: 73 },
  ];
  return (
    <div style={{ fontFamily: HAND, display: "flex", flexDirection: "column", gap: 9 }}>
      {rows.map((r, i) => (
        <div key={r.name} style={{ display: "grid", gridTemplateColumns: "150px 1fr 44px", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 15, color: "var(--text-primary)", lineHeight: 1.15 }}>
            {r.name}
            <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>{r.tasks}</span>
          </div>
          <div style={{ height: 18, borderRadius: 999, background: "var(--surface-5)", border: "1.5px solid var(--border-strong)", overflow: "hidden" }}>
            <div
              style={{
                width: `${r.score}%`,
                height: "100%",
                borderRadius: 999,
                background: BLUE,
                opacity: 0.55,
                transform: `rotate(${tilt(i) * 0.15}deg)`,
              }}
            />
          </div>
          <div style={{ fontSize: 16, color: BLUE, textAlign: "right" }}>{r.score}%</div>
        </div>
      ))}
    </div>
  );
}

/* ── References ───────────────────────────────────────────────────────────── */

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "H. Wang, Q. Mang, A. Cheung, K. Sen, D. Song, “How We Broke Top AI Agent Benchmarks,” UC Berkeley Center for Responsible Decentralized Intelligence (April 2026). Used for the exploit agent scoring 100% on six of eight benchmarks with zero LLM calls, the FieldWorkArena empty-object exploit across all 890 tasks, the SWE-bench conftest.py hook, the WebArena file:// read, and the per-benchmark scores.",
    href: "https://rdi.berkeley.edu/blog/trustworthy-benchmarks-cont/",
    label: "rdi.berkeley.edu/blog/trustworthy-benchmarks-cont",
  },
  {
    text: "S. Singh et al., “The Leaderboard Illusion.” Used for the 2 million battles across 243 models, Meta testing 27 private Llama-4 variants, and the finding that extra Arena data can lift Arena scores by up to 112% relative without transferring to broader benchmarks.",
    href: "https://arxiv.org/abs/2504.20879",
    label: "arxiv.org/abs/2504.20879",
  },
  {
    text: "“Does SWE-Bench-Verified Test Agent Ability or Model Memory?” Used for models scoring about three times higher on SWE-bench Verified than on contamination-controlled benchmarks, and being roughly six times better at naming the edited files with no project context.",
    href: "https://arxiv.org/abs/2512.10218",
    label: "arxiv.org/abs/2512.10218",
  },
  {
    text: "A. Gema et al., “Are We Done with MMLU?” Used for the estimate that 6.49% of MMLU questions contain errors, and that 57% of the analysed Virology questions were faulty, including 33% with a wrong ground-truth answer.",
    href: "https://arxiv.org/abs/2406.04127",
    label: "arxiv.org/abs/2406.04127",
  },
  {
    text: "“Reward Hacking Benchmark: Measuring Exploits in LLM Agents with Tool Use.” Used for the spread in exploit rates across 13 frontier models, from 0% to 13.9%, depending on post-training style.",
    href: "https://arxiv.org/abs/2605.02964",
    label: "arxiv.org/abs/2605.02964",
  },
];

export default function Post() {
  return (
    <>
      <Lead>
        In April 2026 a team at Berkeley built an agent that scored about 100 percent on six of the eight most
        cited AI agent benchmarks. It did not solve a single task. It made zero calls to a language model.
      </Lead>

      <P>
        The agent was not smart. It was a small program that read how each benchmark decides whether you passed,
        then handed back whatever that check happened to look for. On FieldWorkArena, the grader only checked one
        thing: did the last message come from the assistant. It never read the message. So the agent sent back{" "}
        <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.9em" }}>{"{}"}</code>, an empty object, and
        scored 100 percent on all 890 tasks.<Fn n={1} />
      </P>
      <P>
        That is the whole story in one line, and it is worth sitting with. The benchmark was supposed to measure
        whether an agent can do field work. What it actually measured was whether a message had the right sender
        attached. Those are not the same test. For a long time nobody noticed, because the numbers looked
        reasonable and the numbers were the thing everyone watched.
      </P>

      <Figure i={0} caption="The FieldWorkArena bug in miniature. Pick a lazy answer, then flip the toggle and watch the score fall through the floor.">
        <EvalHarness />
      </Figure>

      <Section title="Most of the score was plumbing">
        <P>
          The misconception worth killing is that a leaderboard number measures agent capability. A lot of the
          time it measures plumbing: how the harness sets up a task, runs the answer, and decides what counts.
          The Berkeley work pulled that apart benchmark by benchmark.
        </P>
        <P>
          On SWE-bench Verified and SWE-bench Pro, the agent and the grading tests run in the same container, and
          the agent runs first. So a ten-line{" "}
          <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.9em" }}>conftest.py</code> that quietly
          rewrites every test result to &ldquo;passed&rdquo; gives you 100 percent on 500 and 731 tasks. On
          WebArena, the correct answer sits in a local JSON file, so the agent just opened a{" "}
          <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.9em" }}>file://</code> URL and read it
          instead of browsing anything.<Fn n={1} /> None of this is clever. It is the kind of thing a bored
          intern finds in an afternoon. The point is that the benchmarks we use to rank frontier models were open
          to it.
        </P>
        <Figure i={1} caption="One exploit agent, eight benchmarks, zero tasks actually solved. Six of the eight scored a flat 100 percent.">
          <ScoreBars />
        </Figure>
        <P>
          The authors put it plainly: the benchmarks we rely on to measure AI capability are themselves
          vulnerable to the very capabilities they claim to measure.<Fn n={1} /> An agent good enough to be worth
          testing is also good enough to read the test and game it.
        </P>
      </Section>

      <Section title="It is not one broken grader">
        <P>
          It would be easier if this were a single sloppy benchmark. It is not. The same gap, between what a
          number seems to say and what it actually checks, shows up wherever you look.
        </P>
        <P>
          Take memorization. A 2025 study asked whether SWE-bench Verified tests an agent&rsquo;s ability or its
          memory, since many of those bug fixes are public GitHub history that the models trained on. Models
          scored about three times higher on SWE-bench Verified than on benchmarks built to avoid that overlap,
          and were roughly six times better at naming the exact files to edit with no information about the
          project.<Fn n={3} /> That is not issue-solving skill. That is recall.
        </P>
        <P>
          Or take the leaderboard everyone quotes. The Leaderboard Illusion looked at two million Chatbot Arena
          battles and found the big labs got to test many private model variants and keep only the best score.
          Meta tried 27 variants before the Llama-4 release.<Fn n={2} /> Extra Arena data could lift a
          model&rsquo;s Arena score by up to 112 percent relative, while the same tuning did not carry over to
          broader tests. The ranking was partly measuring who had the most attempts.
        </P>
        <P>
          Even the ground truth can be wrong. When researchers re-checked MMLU, the benchmark behind countless
          &ldquo;PhD-level&rdquo; headlines, they estimated 6.49 percent of its questions contained errors. In
          the virology section, 57 percent of the questions they examined were faulty, a third of them with a
          flat-out wrong answer key.<Fn n={4} /> A model that disagrees with a broken answer is marked wrong for
          being right.
        </P>
      </Section>

      <Section title="What a number is supposed to mean">
        <P>
          I am not saying benchmarks are useless or that everyone is cheating. Most are built in good faith by
          people who want a real signal. The problem is structural. A benchmark is a proxy, and the moment a
          proxy becomes the target, it stops measuring the thing and starts measuring itself. Agents make this
          worse because they are built to find the shortest path to the reward, and a weak grader is just another
          shortcut. One study of reward hacking across 13 frontier models found exploit rates from 0 to 14
          percent depending only on how the model was trained.<Fn n={5} /> The behaviour is already in the
          models. The benchmarks just hand it an opening.
        </P>
        <Callout>
          The score said 100 percent. The agent solved nothing. Both were true at the same time, and only one of
          them mattered.
        </Callout>
        <P>
          So treat the headline number as a claim, not a result. Ask what the grader actually checks. Ask whether
          the task could have leaked into training. Ask whether a lazy answer would pass. The useful version of a
          benchmark is not a single percentage on a slide. It is the trace underneath it: what was asked, what
          the agent did, what got run, and how the pass was decided. If you cannot see that, you are trusting the
          summary, and the summary is the easiest part to fake.
        </P>
        <p style={{ margin: "8px 0 0", fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>
          A number you cannot inspect is not a measurement. It is a vibe.
        </p>
      </Section>

      <Callout>
        CATE is built for exactly this kind of looking: agents, terminals, editors, and diffs as first-class
        windows on one canvas, so you can watch what an agent actually did instead of trusting the score it
        printed at the end.
      </Callout>
      <CTA label="Meet CATE" href="/" />

      <Section title="References">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
